import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import {
  useGetAllBuildingsById,
  useGetAllLocationsDropdown,
  useGetAllSystemDetailCodesDropdown,
} from "../../hooks/common/query";
import { mapToOptions } from "../../lib/functions";
import { Button } from "../button";
import {
  useGetAllEvacuationEventsCodes,
  useGetEvacuationEventReport,
  useGetMusterPointsDropdownByLocation,
} from "../../hooks/emergency-evacuation/query";
import { SYSTEM_DETAIL_CODES } from "../../constants/common";

interface SearchEvacuationReportCardProps {
  setAllEvacuationReports: Dispatch<SetStateAction<never[]>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  pageSize: number;
  setPage: Dispatch<SetStateAction<number>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

const SearchEvacuationReportCardSchema = Yup.object({
  event: Yup.string(),
  startDate: Yup.string(),
  endDate: Yup.string(),
  location: Yup.string(),
  building: Yup.string(),
  status: Yup.string(),
  musterPoint: Yup.string(),
});

const initialState = {
  event: "",
  startDate: "",
  endDate: "",
  location: "",
  building: "",
  status: "",
  musterPoint: "",
};

const SearchEvacuationReportCard = ({
  setAllEvacuationReports,
  page,
  setPages,
  search,
  setCount,
  pageSize,
  setPage,
  setIsLoading,
}: SearchEvacuationReportCardProps) => {
  const {
    control,
    formState: { errors },
    watch,
    reset,
  } = useFormWithSchema(SearchEvacuationReportCardSchema);
  const { t } = useTranslation();

  const [selectedBuildingId, setSelectedBuildingId] = useState<string | number>(
    0,
  );
  const [selectedKey, setSelectedKey] = useState<string>("");

  const { data: allLocations } = useGetAllLocationsDropdown();
  const { data: allEvacuationEventsCodes } = useGetAllEvacuationEventsCodes({
    IsGetAll: true,
  });
  const { data: allEventStatuses } = useGetAllSystemDetailCodesDropdown({
    encryptedId:
      SYSTEM_DETAIL_CODES.EMERGENCY_EVACUATION_EVENT_PARITICIPANT_STATUSES,
  });

  const event = watch("event");
  const musterPoint = watch("musterPoint");
  const location = watch("location");
  const building = watch("building");
  const status = watch("status");
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const { data: allMusterPoints } = useGetMusterPointsDropdownByLocation({
    n_LocationID: location ? Number(location) : 0,
    n_BuildingID: selectedBuildingId ? Number(selectedBuildingId) : 0,
  });

  const { data: evacuationEvents, isPending: isEvacuationEventsLoading } =
    useGetEvacuationEventReport({
      n_LocationID: location ? Number(location) : 0,
      n_BuildingID: building ? Number(building) : 0,
      s_EvacuationCode: event,
      s_MusterPointIDs: musterPoint,
      s_EvacuationStatus: status,
      dt_EventStartTime: startDate,
      dt_EventEndTime: endDate,
      pageSize: pageSize,
      pageNumber: page,
      search: search,
    });

  useEffect(() => {
    setIsLoading?.(isEvacuationEventsLoading);
  }, [isEvacuationEventsLoading, setIsLoading]);

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: location ? location.toString() : "",
  });

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, building, location, status, setPage]);

  useEffect(() => {
    if (evacuationEvents) {
      setAllEvacuationReports(evacuationEvents.data || []);
      setPages(evacuationEvents.pagination?.pages || 1);
      setCount(evacuationEvents.pagination?.count || 0);
    }
  }, [setAllEvacuationReports, setPages, setCount, evacuationEvents]);

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings,
          )
        : [],
    [allBuildings],
  );

  const evacuationStatuses = useMemo(() => {
    return mapToOptions(
      "s_Desc_E",
      "s_Desc_A",
      "n_DetailedCode",
      allEventStatuses,
    );
  }, [allEventStatuses]);

  const musterPoints = useMemo(
    () =>
      mapToOptions(
        "s_MusterPointName_En",
        "s_MusterPointName_Ar",
        "n_MusterPointID",
        allMusterPoints,
      ),
    [allMusterPoints],
  );

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations,
      ),
    [allLocations],
  );

  const evacuationEventsCodes = useMemo(
    () =>
      mapToOptions(
        "s_EvacuationCode",
        "s_EvacuationCode",
        "s_EvacuationCode",
        allEvacuationEventsCodes,
      ),
    [allEvacuationEventsCodes],
  );

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
              <Controller
                name="event"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={evacuationEventsCodes}
                    value={
                      value
                        ? evacuationEventsCodes?.find(
                            (item) => item.value === value,
                          )?.labelEn
                        : ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("event")}
                  />
                )}
              />

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("location")}
                  />
                )}
              />

              <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      buildings?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setSelectedBuildingId(value);
                    }}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              />

              <Controller
                name="musterPoint"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    options={musterPoints}
                    onChange={onChange}
                    label={t("musterPoint")}
                    value={
                      musterPoints?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                  />
                )}
              />

              <Controller
                name="startDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("startDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  />
                )}
              />

              <Controller
                name="endDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("endDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  />
                )}
              />

              <Controller
                name="status"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    options={evacuationStatuses}
                    onChange={onChange}
                    label={t("status")}
                    value={
                      evacuationStatuses?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                  />
                )}
              />
            </div>
          </form>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default memo(SearchEvacuationReportCard);
